import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, FileText, Phone, Calendar, ArrowRight, Check } from 'lucide-react';
import { FastTrackForm } from './FastTrackForm';
import { useLanguage } from '../utils/i18n';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function ThankYouPage() {
  const {
    t
  } = useLanguage();
  const [showFastTrack, setShowFastTrack] = useState(false);
  const [fastTrackComplete, setFastTrackComplete] = useState(false);
  // Check if this was a recent resubmission (within 10 minutes)
  const checkRecentSubmission = () => {
    const keys = Object.keys(sessionStorage);
    const emailJsKeys = keys.filter(key => key.startsWith('dermaclinic_emailjs_sent_'));
    if (emailJsKeys.length > 0) {
      const lastKey = emailJsKeys[emailJsKeys.length - 1];
      const lastSubmitTime = sessionStorage.getItem(lastKey);
      if (lastSubmitTime) {
        const timeSinceLastSubmit = Date.now() - parseInt(lastSubmitTime);
        const tenMinutes = 10 * 60 * 1000;
        return timeSinceLastSubmit < tenMinutes;
      }
    }
    return false;
  };
  const isRecentResubmission = checkRecentSubmission();
  const handleAccelerateClick = () => {
    setShowFastTrack(true);
  };
  const handleFastTrackSubmit = () => {
    setFastTrackComplete(true);
    setShowFastTrack(false);
  };
  const handleSkip = () => {
    setShowFastTrack(false);
  };
  return <div className="min-h-screen w-full bg-white" data-id="element-473">
      {/* Header with Logo */}
      <header className="border-b border-[#E6D3B3]/30 bg-white" data-id="element-474">
        <div className="max-w-7xl mx-auto px-4 py-4" data-id="element-475">
          <img src="https://cdn.magicpatterns.com/uploads/g5pmrSprDDvvcpEJz6NJvt/logo-b_w-small.png" alt="DermaClinic" className="h-10 md:h-12 w-auto" data-id="element-476" />
        </div>
      </header>

      <div className="px-4 py-20" data-id="element-477">
        <div className="max-w-4xl mx-auto" data-id="element-478">
          <AnimatePresence mode="wait" data-id="element-479">
            {!showFastTrack ? <motion.div key="confirmation" initial="hidden" animate="visible" exit={{
            opacity: 0,
            y: -20
          }} variants={staggerContainer} data-id="element-480">
                {/* PRIMARY CONFIRMATION MESSAGE */}
                <motion.div variants={fadeInUp} className="mb-12" data-id="element-481">
                  <div className="text-center max-w-2xl mx-auto" data-id="element-482">
                    <div className="w-20 h-20 rounded-full bg-[#C9A961]/10 border-2 border-[#C9A961] flex items-center justify-center mx-auto mb-6 shadow-sm" data-id="element-483">
                      <CheckCircle className="w-10 h-10 text-[#C9A961]" strokeWidth={1.75} data-id="element-484" />
                    </div>
                    <h1 className="text-3xl md:text-4xl font-bold text-[#2E2E2E] mb-4 leading-tight" data-id="element-485">
                      {t('thankYou.title')}
                    </h1>
                    <p className="text-lg text-[#555555] leading-relaxed mb-8" data-id="element-486">
                      {t('thankYou.subtitle')}
                    </p>
                  </div>
                </motion.div>

                {/* CONFIRMATION STATUS BOX */}
                <motion.div variants={fadeInUp} className="mb-12" data-id="element-487">
                  <div className="flex items-center justify-center gap-3 p-6 bg-[#F7F4EF] rounded-xl border-2 border-[#E6D3B3] shadow-sm" data-id="element-488">
                    <CheckCircle className="w-6 h-6 text-[#C9A961] flex-shrink-0" strokeWidth={1.75} data-id="element-489" />
                    <div className="text-left" data-id="element-490">
                      <p className="text-base font-semibold text-[#2E2E2E] mb-1" data-id="element-491">
                        {t('thankYou.statusTitle')}
                      </p>
                      <p className="text-sm text-[#555555]" data-id="element-492">
                        {isRecentResubmission ? t('thankYou.statusRecent') : t('thankYou.statusNew')}
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* NEXT STEPS SECTION */}
                <motion.div variants={fadeInUp} className="mb-12" data-id="element-493">
                  <div className="bg-white rounded-2xl p-8 md:p-10 border-2 border-[#E6D3B3] shadow-lg" data-id="element-494">
                    <h2 className="text-2xl font-bold text-[#2E2E2E] mb-6 text-center" data-id="element-495">
                      {t('thankYou.nextStepsTitle')}
                    </h2>

                    <div className="space-y-4 mb-6" data-id="element-496">
                      <div className="flex items-start gap-3" data-id="element-497">
                        <div className="w-6 h-6 rounded-full bg-[#C9A961]/10 border border-[#C9A961] flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-498">
                          <Check className="w-4 h-4 text-[#C9A961]" strokeWidth={2} data-id="element-499" />
                        </div>
                        <p className="text-[#555555] leading-relaxed" data-id="element-500">
                          {t('thankYou.step1')}
                        </p>
                      </div>

                      <div className="flex items-start gap-3" data-id="element-501">
                        <div className="w-6 h-6 rounded-full bg-[#C9A961]/10 border border-[#C9A961] flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-502">
                          <Check className="w-4 h-4 text-[#C9A961]" strokeWidth={2} data-id="element-503" />
                        </div>
                        <p className="text-[#555555] leading-relaxed" data-id="element-504">
                          {t('thankYou.step2')}
                        </p>
                      </div>

                      <div className="flex items-start gap-3" data-id="element-505">
                        <div className="w-6 h-6 rounded-full bg-[#C9A961]/10 border border-[#C9A961] flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-506">
                          <Check className="w-4 h-4 text-[#C9A961]" strokeWidth={2} data-id="element-507" />
                        </div>
                        <p className="text-[#555555] leading-relaxed" data-id="element-508">
                          {t('thankYou.step3')}
                        </p>
                      </div>
                    </div>

                    <p className="text-xs text-[#888888] text-center leading-relaxed" data-id="element-509">
                      {t('thankYou.checkSpam')}
                    </p>
                  </div>
                </motion.div>

                {/* WHAT TO EXPECT SECTION */}
                <motion.div variants={fadeInUp} className="bg-[#F7F4EF] rounded-2xl p-8 md:p-10 mb-12 border-2 border-[#E6D3B3]" data-id="element-510">
                  <h2 className="text-2xl font-bold text-[#2E2E2E] mb-8 text-center" data-id="element-511">
                    {t('thankYou.expectTitle')}
                  </h2>

                  <div className="space-y-6" data-id="element-512">
                    {[{
                  icon: FileText,
                  title: t('thankYou.expect1Title'),
                  desc: t('thankYou.expect1Desc')
                }, {
                  icon: Phone,
                  title: t('thankYou.expect2Title'),
                  desc: t('thankYou.expect2Desc')
                }, {
                  icon: Calendar,
                  title: t('thankYou.expect3Title'),
                  desc: t('thankYou.expect3Desc')
                }].map((item, idx) => <div key={idx} className="flex items-start space-x-4" data-id="element-513">
                        <div className="w-12 h-12 rounded-full bg-white border-2 border-[#E6D3B3] flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-514">
                          <item.icon className="w-6 h-6 text-[#C9A961]" strokeWidth={1.75} data-id="element-515" />
                        </div>
                        <div className="flex-1" data-id="element-516">
                          <h3 className="font-semibold text-[#2E2E2E] mb-1 text-lg" data-id="element-517">
                            {item.title}
                          </h3>
                          <p className="text-sm text-[#555555] leading-relaxed" data-id="element-518">
                            {item.desc}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </motion.div>

                {/* CONTACT INFO */}
                <motion.div variants={fadeInUp} className="text-center" data-id="element-519">
                  <p className="text-sm text-[#888888] mb-2" data-id="element-520">
                    {t('thankYou.contactTitle')}
                  </p>
                  <p className="text-base text-[#2E2E2E] font-medium" data-id="element-521">
                    {t('thankYou.contactSubtitle')}
                  </p>
                </motion.div>
              </motion.div> : <motion.div key="fasttrack" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} data-id="element-522">
                <FastTrackForm onSubmit={handleFastTrackSubmit} onSkip={handleSkip} data-id="element-523" />
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 px-4 bg-[#F7F4EF] border-t border-[#E6D3B3]/30" data-id="element-524">
        <div className="max-w-7xl mx-auto text-center" data-id="element-525">
          <img src="https://cdn.magicpatterns.com/uploads/g5pmrSprDDvvcpEJz6NJvt/logo-b_w-small.png" alt="DermaClinic" className="h-8 w-auto mx-auto mb-4 opacity-60" data-id="element-526" />
          <p className="text-xs text-[#888888]" data-id="element-527">{t('thankYou.footer')}</p>
        </div>
      </footer>
    </div>;
}